import { Component } from "react";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import Pagination from "@mui/material/Pagination";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

import { request } from "app/network";
import { useMaterialUIController } from "context";
import AddPointPolicyModal from "./components/AddPointPolicyModal/AddPointPolicyModal";

interface PointPolicyListState {
    policies: any[];
    loading: boolean;
    searchTerm: string;
    pageSize: number;
    pageNumber: number;
    totalCount: number;
    isAddModalOpen: boolean;
    isEdit: boolean;
    selectedPolicy: any;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_point_policy_page_size";

class PointPolicyListScreenClass extends Component<any, PointPolicyListState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 20;

        this.state = {
            policies: [],
            loading: false,
            searchTerm: "",
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            isAddModalOpen: false,
            isEdit: false,
            selectedPolicy: null,
        };
    }

    componentDidMount() {
        this.loadPolicies();
    }

    loadPolicies = async () => {
        try {
            this.setState({ loading: true });

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
            };

            const res = await request("PointPolicy.pagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                const policies = (pagedList.items || []).map((policy: any) => ({
                    seqId: policy.seqId,
                    policyName: policy.policyName || "-",
                    policyType: policy.policyType || "-",
                    conditionType: policy.conditionType || "-",
                    conditionValue: policy.conditionValue || 0,
                    pointAmount: policy.pointAmount || 0,
                    description: policy.description || "",
                    isActive: policy.isActive,
                    isDefault: policy.isDefaultPolicy,
                    priority: policy.priority || 0,
                    startDate: policy.startDate ? new Date(policy.startDate).toLocaleDateString('ko-KR') : "-",
                    endDate: policy.endDate ? new Date(policy.endDate).toLocaleDateString('ko-KR') : "-",
                    tenantName: policy.tenantName || "전체",
                    stepGoalDays: policy.stepGoalDays || 0,
                    videoGoalDays: policy.videoGoalDays || 0,
                    attendanceGoalDays: policy.attendanceGoalDays || 0,
                    isLimitApplied: policy.isLimitApplied ?? true,
                    isSystemPolicy: policy.isSystemPolicy,
                }));

                this.setState({
                    policies,
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({
                    policies: [],
                    totalCount: 0,
                    loading: false
                });
            }
        } catch (error: any) {
            console.error("포인트 정책 목록 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadPolicies();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadPolicies();
        });
    };

    handleOpenAddModal = () => {
        this.setState({
            isAddModalOpen: true,
            isEdit: false,
            selectedPolicy: null
        });
    };

    handleCloseAddModal = () => {
        this.setState({ isAddModalOpen: false });
    };

    handlePolicyAdded = () => {
        this.loadPolicies();
    };

    handleEditClick = (policy: any) => {
        this.setState({
            isAddModalOpen: true,
            isEdit: true,
            selectedPolicy: policy,
        });
    };

    handleDeleteClick = async (seqId: number) => {
        if (window.confirm("정말로 삭제하시겠습니까? (삭제 시 복구할 수 없습니다)")) {
            try {
                await request("PointPolicy.delete", { paramz: { seqId } }); // Assuming the API expects seqId as path param or query. Check API definition. 
                // Wait, API def was url: 'PointPolicy/Delete', paramz: {}. 
                // Usually delete takes ID in URL or query. Current Request helper might append paramz as query strings for DELETE?
                // Actually my valid API definition was `url: 'PointPolicy/Delete'`. If I pass paramz: { seqId }, it usually goes to query string.
                // Revisiting step 1643: `url: 'PointPolicy/Delete'`.
                // Let's assume standard query param for now as per `PointPolicyController.Delete(long seqId)`.

                alert("삭제되었습니다.");
                this.loadPolicies();
            } catch (error) {
                console.error("Delete failed", error);
                alert("삭제 중 오류가 발생했습니다.");
            }
        }
    };

    render() {
        const { loading, totalCount, pageSize, pageNumber } = this.state;

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3}>
                    {/* 검색 영역 */}
                    <Card>
                        <MDBox p={3}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Icon fontSize="medium">stars</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        포인트 정책 목록 ( POINT POLICY LIST )
                                    </MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" onClick={this.handleOpenAddModal}>
                                    <Icon fontSize="small" sx={{ mr: 0.5 }}>
                                        add
                                    </Icon>
                                    정책 추가
                                </MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Autocomplete
                                        disableClearable
                                        value={this.state.pageSize.toString()}
                                        options={["5", "10", "15", "20", "25", "30", "40", "50"]}
                                        renderInput={(params) => (
                                            <MDInput {...params} />
                                        )}
                                        size="small"
                                        sx={{ width: "5rem" }}
                                        onChange={(_, newValue) => {
                                            if (newValue) {
                                                this.handlePageSizeChange(Number(newValue));
                                            }
                                        }}
                                    />
                                    <MDTypography variant="caption" color="secondary">
                                        &nbsp;&nbsp;entries per page
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        </MDBox>
                    </Card>

                    {/* 리스트 영역 */}
                    <MDBox mt={3}>
                        {loading ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <MDBox display="flex" gap={1.5}>
                                    <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                                </MDBox>
                            </MDBox>
                        ) : this.state.policies.length === 0 ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <MDTypography variant="h6" color="text">
                                    등록된 포인트 정책이 없습니다.
                                </MDTypography>
                            </MDBox>
                        ) : (
                            <>
                                <Grid container spacing={3}>
                                    {this.state.policies.map((policy) => {
                                        const gridProps = this.props.miniSidenav
                                            ? { xs: 12, sm: 6, md: 4, lg: 4, xl: 2.4 }
                                            : { xs: 12, sm: 6, md: 4, lg: 4, xl: 4 };

                                        const sxProps = this.props.miniSidenav
                                            ? {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '16.666667%',
                                                    maxWidth: '16.666667%'
                                                }
                                            }
                                            : {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '20%',
                                                    maxWidth: '20%'
                                                }
                                            };

                                        return (
                                            <Grid item {...gridProps} sx={sxProps} key={policy.seqId}>
                                                <Card
                                                    sx={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: 6,
                                                        },
                                                    }}
                                                >
                                                    <MDBox p={2.5} display="flex" flexDirection="column" height="100%">
                                                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                                            <MDTypography variant="h6" fontWeight="medium">
                                                                {policy.policyName}
                                                            </MDTypography>
                                                            <MDBox display="flex" gap={0.5}>
                                                                {policy.isDefault && !policy.isSystemPolicy && (
                                                                    <Chip
                                                                        label="기본"
                                                                        color="primary"
                                                                        size="small"
                                                                        sx={{
                                                                            fontWeight: "medium",
                                                                            backgroundColor: "#2196F3",
                                                                            color: "#ffffff",
                                                                            "& .MuiChip-label": {
                                                                                color: "#ffffff",
                                                                            },
                                                                        }}
                                                                    />
                                                                )}
                                                                {policy.isSystemPolicy && (
                                                                    <Chip
                                                                        label="시스템 정책"
                                                                        color="error"
                                                                        size="small"
                                                                        sx={{
                                                                            fontWeight: "medium",
                                                                            backgroundColor: "#f44336",
                                                                            color: "#ffffff",
                                                                            "& .MuiChip-label": {
                                                                                color: "#ffffff",
                                                                            },
                                                                        }}
                                                                    />
                                                                )}
                                                                {!policy.isSystemPolicy && (
                                                                    <>
                                                                        <Chip
                                                                            label={policy.isLimitApplied ? "제한" : "무제한"}
                                                                            color={policy.isLimitApplied ? "warning" : "info"}
                                                                            size="small"
                                                                            sx={{
                                                                                fontWeight: "medium",
                                                                                color: "#ffffff",
                                                                                "& .MuiChip-label": {
                                                                                    color: "#ffffff",
                                                                                },
                                                                            }}
                                                                        />
                                                                        <Chip
                                                                            label={policy.isActive ? "활성" : "비활성"}
                                                                            color={policy.isActive ? "success" : "default"}
                                                                            size="small"
                                                                            sx={{
                                                                                fontWeight: "medium",
                                                                                color: "#ffffff",
                                                                                "& .MuiChip-label": {
                                                                                    color: "#ffffff",
                                                                                },
                                                                            }}
                                                                        />
                                                                    </>
                                                                )}
                                                            </MDBox>
                                                        </MDBox>

                                                        <Divider sx={{ my: 2 }} />

                                                        <MDBox mb={1.5} flexGrow={1}>
                                                            <MDTypography variant="caption" color="text" display="block">
                                                                정책 타입: <strong>{policy.policyType}</strong>
                                                            </MDTypography>
                                                            <MDTypography variant="caption" color="text" display="block">
                                                                조건: <strong>{policy.conditionType} {policy.conditionValue}</strong>
                                                            </MDTypography>
                                                            <MDTypography variant="caption" color="success" display="block">
                                                                포인트: <strong>+{policy.pointAmount}P</strong>
                                                            </MDTypography>

                                                            {policy.description && (
                                                                <MDBox mt={1.5}>
                                                                    <MDTypography variant="caption" color="text">
                                                                        {policy.description}
                                                                    </MDTypography>
                                                                </MDBox>
                                                            )}
                                                        </MDBox>

                                                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                                            <MDBox>
                                                                <MDTypography variant="caption" color="text" display="block">
                                                                    우선순위: {policy.priority}
                                                                </MDTypography>
                                                                <MDTypography variant="caption" color="text">
                                                                    {policy.startDate} ~ {policy.endDate}
                                                                </MDTypography>
                                                            </MDBox>
                                                            <MDBox display="flex" gap={0.5}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        this.handleEditClick(policy);
                                                                    }}
                                                                    sx={{
                                                                        color: this.props.darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                                                        "&:hover": {
                                                                            backgroundColor: this.props.darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                                                                            color: "#49a3f1",
                                                                        },
                                                                    }}
                                                                >
                                                                    <Icon fontSize="small">edit</Icon>
                                                                </IconButton>
                                                                {!policy.isSystemPolicy && (
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            this.handleDeleteClick(policy.seqId);
                                                                        }}
                                                                        sx={{
                                                                            color: this.props.darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                                                            "&:hover": {
                                                                                backgroundColor: this.props.darkMode ? "rgba(255, 100, 100, 0.1)" : "rgba(255, 0, 0, 0.04)",
                                                                                color: "#f44336",
                                                                            },
                                                                        }}
                                                                    >
                                                                        <Icon fontSize="small">delete</Icon>
                                                                    </IconButton>
                                                                )}
                                                            </MDBox>
                                                        </MDBox>

                                                    </MDBox>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {totalCount > 0 && (
                                    <MDBox mt={4} display="flex" justifyContent="center" alignItems="center" gap={2}>
                                        <Pagination
                                            count={Math.ceil(totalCount / pageSize)}
                                            page={pageNumber}
                                            onChange={(_, page) => this.handlePageChange(page - 1)}
                                            color="primary"
                                            size="large"
                                            showFirstButton
                                            showLastButton
                                            sx={{
                                                '& .MuiPaginationItem-root': {
                                                    color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                                },
                                                '& .Mui-selected': {
                                                    backgroundColor: '#49a3f1 !important',
                                                    color: '#ffffff !important',
                                                },
                                            }}
                                        />
                                    </MDBox>
                                )}
                            </>
                        )}
                    </MDBox>
                </MDBox>
                <Footer />
                <AddPointPolicyModal
                    open={this.state.isAddModalOpen}
                    onClose={this.handleCloseAddModal}
                    onSuccess={this.handlePolicyAdded}
                    darkMode={this.props.darkMode}
                    isEdit={this.state.isEdit}
                    initialData={this.state.selectedPolicy}
                />
            </DashboardLayout >
        );
    }
}

function PointPolicyListScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode, miniSidenav } = controller;

    return <PointPolicyListScreenClass {...props} darkMode={darkMode} miniSidenav={miniSidenav} />;
}

export default PointPolicyListScreen;
